// Подраздел «Комфортной среды»: чем закрашен раздел HSI.
//
// ПАРАМЕТР РАЗДЕЛА, а не четвёртый `ModelKind` — то же решение и по той же
// причине, что у механизмов апвеллинга (`upwellingSelection.ts`): раздел есть
// набор асинхронных эффектов (фон, опрос после «Загрузить», перенацеливание
// probability, `layerRef.owner`, `seriesRef`), и новый раздел размножил бы всю
// эту машинерию.
//
// Список подразделов строится ИЗ КАТАЛОГА слоёв (`group === 'hsi'`), а не из
// перечня здесь: перечень отстанет от каталога, и заведённый на сервере
// подраздел в панели не появится, а снятый оставит мёртвый переключатель,
// отвечающий 400.

import type { LayerCatalogItem } from '../api'

/** Подраздел, как его описывает каталог слоёв. */
export interface HsiKind {
  id: string
  unit: string
  has_depth: boolean
  /** Виды, у которых модель есть. `null` — у всех (сезонная модель). */
  species: number[] | null
}

/** Нынешний индекс комфортной среды — многопараметрическая сезонная модель.
 *
 *  Он и есть умолчание: ссылки `?model=hsi` разосланы задолго до появления
 *  подразделов и обязаны открываться ровно тем, чем открывались.
 */
export const DEFAULT_HSI = 'probability'

/** Карта нейросети. Ключ тот же, что у слоя, палитры и адреса тайла. */
export const NN = 'nn'

/** Подразделы семьи из каталога, в порядке каталога. */
export function hsiKindsFrom(layers: LayerCatalogItem[]): HsiKind[] {
  return layers
    .filter(l => l.group === 'hsi')
    .map(l => ({
      id: l.id,
      unit: l.unit,
      has_depth: l.has_depth,
      // `species` приезжает, ТОЛЬКО когда набор ограничен. Отсутствие поля
      // значит «слой не про виды», а не «ни одного вида»: прочитав пустоту как
      // «нигде», мы спрятали бы сам индекс комфортной среды.
      species: Array.isArray(l.species) ? l.species : null,
    }))
}

/** Обслуживает ли подраздел этот вид.
 *
 *  Вид ещё не выбран (`null`) — подраздел показываем: гасить его до выбора
 *  значило бы мигать панелью на каждом входе в раздел.
 */
export function servesSpecies(kind: HsiKind, species: number | null): boolean {
  if (kind.species === null) return true
  if (species == null) return true
  return kind.species.includes(species)
}

/**
 * Привести выбор к тому, что каталог и вид действительно предлагают.
 *
 * При ПУСТОМ каталоге возвращает вход КАК ЕСТЬ: каталог приезжает запросом, и до
 * его приезда обнулять выбор нельзя — ссылка `?hsi=nn` открылась бы на сезонной
 * модели. Та же беда, из-за которой написаны `clampSelection` и `clampOverlays`.
 *
 * Смена вида на тот, у которого модели нет, возвращает к умолчанию: карта иначе
 * осталась бы пустой при живом переключателе, а это неотличимо от поломки.
 */
export function clampHsi(kind: string, catalog: HsiKind[],
                         species: number | null): string {
  if (catalog.length === 0) return kind
  const chosen = catalog.find(k => k.id === kind)
  if (chosen && servesSpecies(chosen, species)) return kind
  const fallback = catalog.find(k => k.id === DEFAULT_HSI)
  if (fallback && servesSpecies(fallback, species)) return DEFAULT_HSI
  const any = catalog.find(k => servesSpecies(k, species))
  return any ? any.id : DEFAULT_HSI
}

/** Подразделы, которые имеет смысл показывать при этом виде.
 *
 *  Решение владельца 05.09.2026: подраздел виден ТОЛЬКО там, где модель есть.
 *  Показанный у вида без модели, он дал бы пустую карту с кнопкой «Загрузить»,
 *  которая её не исправит, — то есть немой отказ.
 */
export function visibleKinds(catalog: HsiKind[], species: number | null): HsiKind[] {
  return catalog.filter(k => servesSpecies(k, species))
}
